/**
 * Navigation Phase Component
 * FEA-212: Phase section for 6-phase FMEA workflow navigation
 * Shows phase header with status, items with indicators, and meeting button
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { NavPhase, NavItem } from './navigationConfig';

export type PhaseStatus = 'complete' | 'active' | 'pending';

interface NavigationPhaseProps {
  phase: NavPhase;
  isExpanded: boolean;
  isSidebarExpanded: boolean;
  onToggle: () => void;
  currentPath: string;
  activePhaseNumber: number;
}

/**
 * Get phase status based on position relative to active phase
 * FEA-216: Progress snake shows journey through phases
 * - Phases BEFORE active = complete (green)
 * - Active phase = active (cyan pulse)
 * - Phases AFTER active = pending (gray)
 */
function getPhaseStatus(phase: NavPhase, activePhaseNumber: number): PhaseStatus {
  if (activePhaseNumber === 0) {
    // No active phase - all pending
    return 'pending';
  }

  if (phase.number < activePhaseNumber) {
    return 'complete';
  }

  if (phase.number === activePhaseNumber) {
    return 'active';
  }

  return 'pending';
}

/**
 * Get total badge count for a phase
 */
function getPhaseBadgeCount(phase: NavPhase): number {
  return phase.items.reduce((sum, item) => {
    if (typeof item.badge === 'number') return sum + item.badge;
    if (typeof item.badge === 'string') return sum + parseInt(item.badge, 10) || 0;
    return sum;
  }, 0);
}

export const NavigationPhase: React.FC<NavigationPhaseProps> = ({
  phase,
  isExpanded,
  isSidebarExpanded,
  onToggle,
  currentPath,
  activePhaseNumber,
}) => {
  const phaseStatus = getPhaseStatus(phase, activePhaseNumber);
  const badgeCount = getPhaseBadgeCount(phase);
  const isActivePhase = phaseStatus === 'active';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onToggle();
    }
    if (e.key === 'ArrowDown' && isExpanded) {
      e.preventDefault();
      const firstLink = document.querySelector(`#nav-phase-${phase.id} a`) as HTMLElement;
      firstLink?.focus();
    }
  };

  // Determine snake segment status (for line to next phase)
  // Segment is complete if this phase is complete, active if this phase is active
  const snakeSegmentStatus = phaseStatus;

  return (
    <li className="nav-phase" role="none">
      {/* FEA-216: Snake node */}
      <span
        className={`nav-snake-node nav-snake-node--${phaseStatus}`}
        data-testid={`nav-snake-node-${phase.id}`}
        aria-hidden="true"
      />
      {/* FEA-216: Snake segment (line to next phase) */}
      <span
        className={`nav-snake-segment nav-snake-segment--${snakeSegmentStatus}`}
        data-testid={`nav-snake-segment-${phase.id}`}
        aria-hidden="true"
      />

      <div className="nav-phase-header-row">
        <button
          className={`nav-phase-header nav-phase-header--${phaseStatus} ${isExpanded ? 'expanded' : ''}`}
          onClick={onToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
          aria-controls={`nav-phase-${phase.id}`}
          aria-haspopup="menu"
          title={phase.label}
          role="menuitem"
        >
          {/* Phase icon (FEA-216: semantic icons) */}
          <span className={`nav-phase-number nav-phase-number--${phaseStatus}`} aria-label={`Phase ${phase.number}: ${phase.label}`}>
            {phase.icon}
          </span>

          {isSidebarExpanded && (
            <>
              <span className="nav-phase-label">{phase.label}</span>

              {/* Status indicator - only show for complete/active, not pending */}
              {phaseStatus !== 'pending' && (
                <span className={`nav-phase-status nav-phase-status--${phaseStatus}`}>
                  {phaseStatus === 'complete' && '✓'}
                  {phaseStatus === 'active' && '●'}
                </span>
              )}

              {/* Badge count if any */}
              {badgeCount > 0 && (
                <span className="nav-phase-badge-inline">{badgeCount}</span>
              )}

              <span className={`nav-phase-chevron ${isExpanded ? 'expanded' : ''}`} aria-hidden="true">
                ▼
              </span>
            </>
          )}

          {/* Badge count when collapsed */}
          {!isSidebarExpanded && badgeCount > 0 && (
            <span className="nav-phase-badge-collapsed">{badgeCount}</span>
          )}
        </button>
      </div>

      {/* Phase items */}
      {isSidebarExpanded && (
        <ul
          id={`nav-phase-${phase.id}`}
          className={`nav-phase-items ${isExpanded ? 'expanded' : 'collapsed'}`}
          role="menu"
          aria-label={`${phase.label} navigation items`}
        >
          {phase.items.map((item) => (
            <NavigationPhaseItem
              key={item.path}
              item={item}
              currentPath={currentPath}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

interface NavigationPhaseItemProps {
  item: NavItem;
  currentPath: string;
}

const NavigationPhaseItem: React.FC<NavigationPhaseItemProps> = ({ item, currentPath }) => {
  const isActive = item.path === currentPath;
  const itemStatus = isActive ? 'active' : (item.status || 'not-started');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (e.currentTarget.parentElement?.previousElementSibling?.querySelector('a') ||
        e.currentTarget.closest('.nav-phase')?.querySelector('.nav-phase-header')) as HTMLElement;
      prev?.focus();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.parentElement?.nextElementSibling?.querySelector('a') as HTMLElement;
      next?.focus();
    }
  };

  return (
    <li role="none">
      <NavLink
        to={item.path}
        className={({ isActive: routeActive }) =>
          `nav-phase-item nav-phase-item--${itemStatus} ${routeActive ? 'active' : ''}`
        }
        data-testid={item.testId}
        aria-label={`${item.label} page`}
        title={item.label}
        role="menuitem"
        onKeyDown={handleKeyDown}
      >
        <span className="nav-phase-item-icon" aria-hidden="true">
          {item.icon}
        </span>
        <span className="nav-phase-item-label">{item.label}</span>

        {/* Status indicator */}
        <span className={`nav-phase-item-status nav-phase-item-status--${itemStatus}`}>
          {itemStatus === 'complete' && '✓'}
          {isActive && '🔵'}
          {itemStatus === 'not-started' && '○'}
        </span>

        {/* Badge */}
        {item.badge !== undefined && (
          <span className="nav-phase-item-badge" data-testid={`${item.testId}-badge`}>
            {item.badge}
          </span>
        )}
      </NavLink>
    </li>
  );
};
